// import library
import axios from "axios";
import { jwtDecode } from "jwt-decode";
// file
import { env } from "../env";

const BASE_URL = env.REACT_APP_CLIENT_API_URL;

// Helper — get token from local or session storage
const getToken = () => {
    return localStorage.getItem("postgreToken") || sessionStorage.getItem("postgreToken");
};

// Helper — remove token & user info from both storages
const clearAuthData = () => {
    localStorage.removeItem("userInfo");
    localStorage.removeItem("token");
    localStorage.removeItem("postgreToken");
    sessionStorage.removeItem("userInfo");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("postgreToken");
};

// Helper — check if JWT expired
const isTokenExpired = () => {
    const token = getToken();
    if (!token) return true;

    try {
        const { exp } = jwtDecode(token);
        if (!exp) return false; // if exp doesn't exist, assume valid
        return Date.now() >= exp * 1000;
    } catch {
        return false; // decoding error shouldn't auto-expire
    }
};

// Helper — refresh token by calling login API
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
    refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
    refreshSubscribers.forEach((callback) => callback(newToken));
    refreshSubscribers = [];
};

const refreshToken = async () => {
    try {
        // Call login API to get fresh token with static credentials
        const response = await axios.post(`${BASE_URL}/login`, {
            username: "bakalarosapi",
            password: "Bk!1(27056!",
        }, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.data) {
            throw new Error("Login failed");
        }

        if (response.data && response.data.token) {
            const newToken = response.data.token;
            // Store in the same location as original token
            if (localStorage.getItem("postgreToken")) {
                localStorage.setItem("postgreToken", newToken);
            } else {
                sessionStorage.setItem("postgreToken", newToken);
            }
            return newToken;
        }
        return null;
    } catch (error) {
        console.error("Error refreshing token:", error);
        return null;
    }
};

// Create Axios instance
const api = axios.create({
    baseURL: `${BASE_URL}/`,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request Interceptor
api.interceptors.request.use(async (config) => {
    const token = getToken();

    if (token) {
        // If token expired — refresh it
        if (isTokenExpired()) {
            if (!isRefreshing) {
                isRefreshing = true;
                const newToken = await refreshToken();
                isRefreshing = false;

                if (newToken) {
                    onTokenRefreshed(newToken);
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return config;
                } else {
                    // Only clear and reload if refresh fails
                    clearAuthData();
                    window.location.reload();
                    return Promise.reject("Token refresh failed");
                }
            } else {
                // Wait for token refresh to complete
                return new Promise((resolve) => {
                    subscribeTokenRefresh((newToken) => {
                        config.headers.Authorization = `Bearer ${newToken}`;
                        resolve(config);
                    });
                });
            }
        }
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
},(error) => Promise.reject(error));

// Response Interceptor
api.interceptors.response.use((response) => response, async (error) => {
    const originalRequest = error.config;

    if (error.response) {
        const { status } = error.response;

        // Skip refresh if this is the login request
        const isSignInRoute = originalRequest.url?.includes("/signin") || originalRequest.url?.includes("/login");

        // Handle unauthorized — try to refresh token
        if (status === 401 && !isSignInRoute && !originalRequest._retry) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                const newToken = await refreshToken();
                isRefreshing = false;

                if (newToken) {
                    onTokenRefreshed(newToken);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);
                } else {
                    // Only clear and reload if refresh fails
                    clearAuthData();
                    window.location.reload();
                    return Promise.reject(error);
                }
            } else {
                // Wait for token refresh to complete
                return new Promise((resolve) => {
                    subscribeTokenRefresh((newToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(api(originalRequest));
                    });
                });
            }
        }
    }
    return Promise.reject(error);
  }
);

// Helper — convert JSON → x-www-form-urlencoded
const toUrlEncoded = (data) => new URLSearchParams(data).toString();

// API Service Methods
const apiService = {
    get: (url, config = {}) => api.get(url, config),

    post: (url, data, config = {}) => api.post(url, data, config),

    put: (url, data, config = {}) => api.put(url, data, config),

    delete: (url, config = {}) => api.delete(url, config),

    postFormUrlEncoded: (url, data, config = {}) =>
        api.post(url, toUrlEncoded(data), {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            ...config,
        }),

    postFormData: (url, data, config = {}) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });
        return api.post(url, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            ...config,
        });
    },
};

export default apiService;
